import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../App';
import { ConfirmModal } from './Modals';
import { RESELLER_PERM_LIST, groupPerms, withImplications } from '../resellerPerms';
import { useIsMobile } from '../utils';

const lbl = { display: 'block', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', marginBottom: 6, fontFamily: 'var(--mono)' };
const inp = { width: '100%', background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text)', padding: '10px 12px', fontFamily: 'var(--mono)', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
const btn = { background: 'transparent', border: '1px solid var(--border)', color: 'var(--text)', padding: '6px 12px', fontSize: 11, fontFamily: 'var(--mono)', cursor: 'pointer' };

const EMPTY_PERMS = Object.fromEntries(RESELLER_PERM_LIST.map(p => [p.key, false]));

// #210 etapa 2 — alta / edición de un usuario de la empresa. Los permisos que superan el techo que
// fijó OpenBackup van deshabilitados (el backend también los rechaza con 400).
function CompanyUserModal({ user, ceiling, companyId, onClose, onSaved }) {
  const isMobile = useIsMobile();
  const isNew = !user;
  const [form, setForm] = useState({
    email: user?.email || '', display_name: user?.display_name || '', active: user ? user.active : true,
    perms: { ...EMPTY_PERMS, can_view_clients: !!ceiling?.can_view_clients, can_view_reports: !!ceiling?.can_view_reports,
             ...(user?.reseller_permissions || {}) },
  });
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const togglePerm = (k) => setForm(f => ({ ...f, perms: withImplications({ ...f.perms, [k]: !f.perms[k] }) }));
  const over = (k) => !ceiling?.[k];

  const submit = async () => {
    if (!form.email.trim()) { setError('El email es obligatorio'); return; }
    setSaving(true); setError('');
    try {
      let created = null;
      if (isNew) {
        created = await api.createUser({
          email: form.email.trim(), role: 'reseller', client_id: companyId,
          display_name: form.display_name, reseller_permissions: form.perms,
        });
      } else {
        await api.updateUser(user.id, {
          email: form.email.trim(), role: 'reseller', client_id: companyId,
          display_name: form.display_name, active: form.active,
        });
        await api.updateResellerPerms(user.id, form.perms);
      }
      onSaved(created); onClose();
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(4px)', padding: 16 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', width: isMobile ? '95vw' : 520, maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', fontFamily: 'var(--mono)', fontSize: 14, letterSpacing: 1 }}>
          {isNew ? 'NUEVO USUARIO DE LA EMPRESA' : 'EDITAR USUARIO'}
        </div>
        <div style={{ padding: 24 }}>
          {error && <div style={{ background: 'rgba(255,77,109,0.1)', border: '1px solid var(--red)', color: 'var(--red)', padding: '10px 14px', fontSize: 12, fontFamily: 'var(--mono)', marginBottom: 16 }}>{error}</div>}
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="cu-name" style={lbl}>Nombre</label>
            <input id="cu-name" style={inp} value={form.display_name} onChange={e => set('display_name', e.target.value)} />
          </div>
          <div style={{ marginBottom: 14 }}>
            <label htmlFor="cu-email" style={lbl}>Email</label>
            <input id="cu-email" type="email" style={inp} value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          {isNew ? (
            <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)', marginBottom: 14 }}>
              No se define contraseña: el usuario recibe un mail con un enlace para crearla.
            </div>
          ) : (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, fontSize: 13, cursor: 'pointer' }}>
              <input type="checkbox" checked={form.active} onChange={e => set('active', e.target.checked)} style={{ accentColor: 'var(--accent)' }} />
              Usuario activo
            </label>
          )}
          <div style={{ fontSize: 11, color: 'var(--muted)', fontFamily: 'var(--mono)', margin: '4px 0 12px' }}>
            En su primer ingreso debe configurar MFA y aceptar los términos. Los permisos en gris superan el techo que fijó OpenBackup para la empresa.
          </div>
          {Object.entries(groupPerms()).map(([group, items]) => (
            <div key={group} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 9, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--yellow)', marginBottom: 6, fontFamily: 'var(--mono)', opacity: 0.8 }}>{group}</div>
              {items.map(({ key, label }) => (
                <label key={key} title={over(key) ? 'Supera el techo de la empresa' : undefined}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6, padding: '6px 10px', background: 'var(--bg)', border: '1px solid var(--border)', opacity: over(key) ? 0.45 : 1, cursor: over(key) ? 'not-allowed' : 'pointer' }}>
                  <input type="checkbox" checked={!!form.perms[key]} disabled={over(key)} onChange={() => togglePerm(key)}
                    style={{ accentColor: 'var(--yellow)' }} />
                  <span style={{ fontSize: 13 }}>{label}</span>
                </label>
              ))}
            </div>
          ))}
        </div>
        <div style={{ padding: '14px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
          <button onClick={onClose} style={btn}>CANCELAR</button>
          <button onClick={submit} disabled={saving}
            style={{ ...btn, background: 'var(--accent)', borderColor: 'var(--accent)', color: 'var(--bg)', fontWeight: 700 }}>
            {saving ? 'GUARDANDO...' : 'GUARDAR →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// «Usuarios de mi empresa»: sólo para quien tiene can_manage_account.
export function CompanyUsersSection({ showTitle = true, onLoaded }) {
  const isMobile = useIsMobile();
  const { me } = useAuth();
  const [users, setUsers]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);        // 'new' | user
  const [confirm, setConfirm] = useState(null);
  const [msg, setMsg]         = useState(null);        // { ok, text }

  const load = useCallback(async () => {
    try { const list = await api.getCompanyUsers(); setUsers(list); if (onLoaded) onLoaded(list); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
    finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const deactivate = async (u) => {
    try { await api.deleteUser(u.id); setMsg({ ok: true, text: 'Usuario dado de baja' }); load(); }
    catch (e) { setMsg({ ok: false, text: e.message }); }
  };

  const status = (u) => (u.active ? 'Activo' : 'Baja');

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div>
          {showTitle && <div style={{ fontFamily: 'var(--mono)', fontSize: 16 }}>Mi empresa ({users.filter(u => u.active).length})</div>}
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 4 }}>Las personas de tu empresa que entran a la consola, dentro del techo de permisos que fija OpenBackup.</div>
        </div>
        <button onClick={() => setModal('new')} style={{ ...btn, borderColor: 'var(--yellow)', color: 'var(--yellow)' }}>+ USUARIO DE MI EMPRESA</button>
      </div>
      {msg && <div style={{ marginBottom: 12, padding: '10px 14px', fontFamily: 'var(--mono)', fontSize: 12, border: `1px solid ${msg.ok ? 'var(--green)' : 'var(--red)'}`, color: msg.ok ? 'var(--green)' : 'var(--red)' }}>{msg.text}</div>}
      {loading ? (
        <div style={{ fontFamily: 'var(--mono)', color: 'var(--muted)', fontSize: 12 }}>CARGANDO...</div>
      ) : isMobile ? (
        <div>
          {users.map(u => (
            <div key={u.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', padding: '14px 16px', marginBottom: 8 }}>
              <div style={{ fontSize: 14 }}>{u.display_name || u.email}</div>
              <div style={{ fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--mono)' }}>{u.email} · {status(u)}</div>
              <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                <button onClick={() => setModal(u)} style={btn}>EDITAR</button>
                {u.active && <button onClick={() => setConfirm(u)} style={{ ...btn, borderColor: 'var(--red)', color: 'var(--red)' }}>DAR DE BAJA</button>}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <thead>
            <tr>{['Nombre', 'Email', 'Estado', ''].map(h => (
              <th key={h} style={{ textAlign: 'left', padding: '10px 14px', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--muted)', fontFamily: 'var(--mono)', borderBottom: '1px solid var(--border)' }}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '10px 14px', fontSize: 13 }}>{u.display_name || '—'}</td>
                <td style={{ padding: '10px 14px', fontSize: 13, fontFamily: 'var(--mono)' }}>{u.email}</td>
                <td style={{ padding: '10px 14px', fontSize: 12, color: u.active ? 'var(--green)' : 'var(--muted)' }}>{status(u)}</td>
                <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                  <button onClick={() => setModal(u)} style={btn}>EDITAR</button>{' '}
                  {u.active && <button onClick={() => setConfirm(u)} style={{ ...btn, borderColor: 'var(--red)', color: 'var(--red)' }}>DAR DE BAJA</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {modal && (
        <CompanyUserModal user={modal === 'new' ? null : modal} ceiling={me?.reseller_ceiling} companyId={me?.client_id}
          onClose={() => setModal(null)} onSaved={(created) => {
            const mail = created?.welcome_email;
            setMsg(mail && !mail.sent
              ? { ok: false, text: `Usuario creado, pero el mail con el enlace no salió (${mail.error || 'error de envío'}). El usuario puede pedir el enlace desde «Olvidé mi contraseña».` }
              : { ok: true, text: mail ? 'Usuario creado: se le envió un mail para definir su contraseña' : 'Guardado' });
            load();
          }} />
      )}
      {confirm && (
        <ConfirmModal title="Dar de baja" danger message={`¿Dar de baja a "${confirm.email}"? Ya no podrá ingresar.`}
          onConfirm={() => { deactivate(confirm); setConfirm(null); }} onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}
